import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import { EventTemplateForm } from '@go-client/components';

import { Popup, Alert, InlineIcon, Tooltip } from '@spark-labs/avocado-ui';
import { camelToSentenceCase, capitalize, truncateWords } from '@go-shared/lib/string';
import {
  bare,
  create,
  update,
  exportEventTemplatesToCSV,
  setArchived,
  uploadEventImage,
  paginateEventTemplates,
} from '@go-client/lib/api/eventTemplate';
import {
  BareEventTemplate,
  DefaultEventDisplayOption,
  EventTemplate,
} from '@go-shared/types/eventTemplate';
import Config from '@go-client/lib/config';

import './EventLibrary.scss';
import { StaffTemplate } from '@go-shared/types/staffTemplate';
import {
  getStaffTemplatesByPropertyId,
  populate as populateStaffTemplate,
} from '@go-client/lib/api/staffTemplate';
import { CopyToClipboardButton } from '@go-client/components/CopyToClipboard';
import {
  BubbleBadge,
  BubbleButton,
  BubbleDebounce,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleInput,
  BubbleTable,
} from '@go-client/components/BubbleUI';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { PartialBy } from '@go-shared/types/typeUtils';
import { usePropertyInfo } from '@go-client/context/PropertyInfoContext';
import { hashStringToColor } from '@go-shared/lib/utils';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import Libraries from './Libraries';
import { canManageLibraries } from '@go-shared/lib/permissions';
import { LibrariesSubNavigation } from '@go-client/components/Navigations/LibrariesSubNavigation';
import BubbleAccordion from '@go-client/components/BubbleUI/BubbleAccordion';
import { BubbleTableFields, BubbleTableTemplate } from '@go-client/components/BubbleUI/BubbleTable';
import { SortOrder } from '@go-shared/types/userActionRecord';
import { PaginationMetadata, DEFAULT_PER_PAGE } from '@go-shared/types/pagination';
import { BubbleTablePaginationFooter } from '@go-client/components/BubbleUI/BubbleTablePaginationFooter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getLocaleUTCDateTimeString } from '@go-shared/lib/time';
import { getSortingIcon } from '@go-shared/lib/paginations';
import AccessGlanceButton from '@go-client/components/AccessGlanceButton';
import { McmEndpoints } from '@go-client/components/Mcm/McmEndpoints';
import { readQs } from '@go-shared/lib/querystring';
import BubbleDrawerBasicContainer from '@go-client/components/BubbleUI/BubbleDrawerHeader';
import PropertyAccessFormik from '@go-client/components/Formik/PropertyAccessFormik';

type EventLibraryPageProps = RouteComponentProps;

interface EventLibraryDataBundle {
  staffTemplates: StaffTemplate[];
  eventTemplates: EventTableItem[];
}

type EventTableItem = {
  id: string;
  title: string;
  _source: EventTemplate;
};

type VisibleFields = {
  [key: string]: boolean;
};

type EventsTableFields = BubbleTableFields<EventTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

type PaginationParams<T> = {
  page: number;
  perPage: number;
  sortBy: keyof T;
  sortDirection: SortOrder;
  archived: boolean;
  startDate?: string;
  endDate?: string;
  search?: string;
  propertyId?: string;
};

export default function EventLibrary(props: EventLibraryPageProps) {
  const { ...rest } = props;

  const { user } = useUserInfo();
  const { activeProperty: property } = useActiveProperty();
  const { allProperties: properties } = usePropertyInfo();
  const serverInfo = useServerInfo();
  const { languages } = serverInfo;

  const [loading, setLoading] = useState(true);

  const [isSaved, setIsSaved] = useState(true);

  const [dataBundle, setDataBundle] = useState<EventLibraryDataBundle | undefined>(undefined);
  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>(
    undefined
  );

  const [showEventTemplateForm, setShowEventTemplateForm] = useState(false);
  const [eventTemplateFormMode, setEventTemplateFormMode] = useState<'create' | 'edit' | undefined>(
    undefined
  );
  const [selectedEventTemplate, setSelectedEventTemplate] = useState<EventTemplate | undefined>(
    undefined
  );
  const [showAccessForm, setShowAccessForm] = useState<EventTemplate | undefined>(undefined);

  const [visibleFields, setVisibleFields] = useState<VisibleFields>({
    access: true,
    title: true,
    description: true,
    segment: true,
    levels: false,
    attributes: false,
    private: true,
    crew: true,
    simple: false,
    duration: false,
    inventoried: false,
    images: true,
    createdAt: false,
    actions: true,
    endpoints: true,
  });

  const queries = readQs();

  const [paginationParams, setPaginationParams] = useState<PaginationParams<BareEventTemplate>>({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    sortBy: 'createdAt',
    sortDirection: SortOrder.DESC,
    startDate: '',
    endDate: '',
    archived: false,
    propertyId: property._id,
    search: typeof queries.search === 'string' ? queries.search : '',
  });

  const contentRef = useRef<HTMLDivElement | null>(null);
  // this will scroll the page to top when search is modified
  useEffect(() => {
    // We want to scroll to top only when we are in page 1 or paginationParams are reset
    if (paginationParams.page !== 1) {
      return;
    }

    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [paginationParams, contentRef]);

  useEffect(() => {
    if (property._id !== paginationParams.propertyId) {
      setPaginationParams({ ...paginationParams, propertyId: property._id });
    }
  }, [property]);

  async function refresh() {
    setShowAccessForm(undefined);
    setLoading(true);
    await fetchDataBundle();
  }

  useEffect(() => {
    refresh();
  }, [paginationParams]);

  async function fetchDataBundle() {
    setLoading(true);
    try {
      const [bareStaffTemplates, eventTemplates] = await Promise.all([
        getStaffTemplatesByPropertyId(property._id),
        paginateEventTemplates({
          page: paginationParams.page,
          perPage: paginationParams.perPage,
          sortBy: paginationParams.sortBy,
          sortDirection: paginationParams.sortDirection,
          propertyId: property._id,
          ...(paginationParams.search && { search: paginationParams.search }),
          ...(paginationParams.archived && { archived: paginationParams.archived }),
          ...(paginationParams.startDate && { startDate: paginationParams.startDate }),
          ...(paginationParams.endDate && { endDate: paginationParams.endDate }),
        }),
      ]);

      let staffTemplates = bareStaffTemplates.map(st => populateStaffTemplate(st, property));

      setPaginationMetadata(eventTemplates.meta);

      const et = eventTemplates.data.map(et => {
        return {
          id: et._id,
          title: et.title,
          _source: et,
        };
      });

      setDataBundle({
        staffTemplates,
        eventTemplates:
          paginationParams.page > 1 ? [...(dataBundle?.eventTemplates ?? []), ...et] : et,
      });
    } finally {
      setLoading(false);
    }
  }

  let showNewEventBtn = canManageLibraries(user);

  const hasTranslations = languages.length > 0;
  const popupAction = eventTemplateFormMode ? capitalize(eventTemplateFormMode) + ' Event' : '';
  const eventTemplateTitle = selectedEventTemplate ? selectedEventTemplate.title : '';
  const popupTitle = `${popupAction}  ${
    eventTemplateFormMode === 'edit' ? '- ' + eventTemplateTitle : ''
  }`;

  const tableFields: Array<EventsTableFields> = [
    { key: 'access' },
    {
      key: 'title',
      tdProps: { style: { minWidth: '200px' } },
      _toggleable: true,
    },
    {
      key: 'description',
      tdProps: { style: { minWidth: '400px' } },
      _toggleable: true,
    },
    {
      key: 'segment',
      _toggleable: true,
    },
    {
      key: 'levels',
      _toggleable: true,
    },
    {
      key: 'attributes',
      _toggleable: true,
    },
    {
      key: 'endpoints',
      _toggleable: true,
    },
    {
      key: 'private',
      _toggleable: true,
    },
    {
      key: 'crew',
      _toggleable: true,
    },
    {
      key: 'simple',
      _toggleable: true,
    },
    { key: 'inventoried', _toggleable: true },
    {
      key: 'duration',
      _toggleable: true,
    },
    { key: 'images', _toggleable: true },
    {
      key: 'createdAt',
      thProps: {
        onClick: () => {
          const sortDirection =
            paginationParams.sortBy === 'createdAt'
              ? paginationParams.sortDirection * -1
              : SortOrder.DESC;
          setPaginationParams({ ...paginationParams, page: 1, sortBy: 'createdAt', sortDirection });
        },
        className: 'whitespace-nowrap',
      },
      render: () => (
        <>
          <span className="cursor-pointer">
            Created
            {paginationParams.sortBy === 'createdAt' && (
              <FontAwesomeIcon
                icon={getSortingIcon(paginationParams.sortDirection)}
                fixedWidth
                size={'sm'}
                className={'cursor-pointer'}
              />
            )}
          </span>
        </>
      ),
      _toggleable: true,
    },
    {
      key: 'actions',
      label: '',
      thProps: { className: 'pl-4-i' },
      tdProps: { className: 'whitespace-nowrap p-1-i pr-4-i text-right' },
      _toggleable: false,
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<EventTableItem>> = [
    {
      key: 'title',
      render: item => {
        return (
          <>
            <div>
              <div>{item.title}</div>
              <CopyToClipboardButton copyText={item._source._id} label="Copy ID" />
            </div>
          </>
        );
      },
    },
    {
      key: 'access',
      render: item => {
        return (
          <>
            <Tooltip text={'Click to manage the access'}>
              <AccessGlanceButton
                item={item._source}
                onClick={() => {
                  setEventTemplateFormMode('edit');
                  setShowAccessForm(item._source);
                }}
              />
            </Tooltip>
          </>
        );
      },
    },
    {
      key: 'description',
      render: item => {
        const words = truncateWords(item._source.description, Config.EVENT_DESCRIPTION_MAX_LENGTH);
        return <div>{words}</div>;
      },
    },
    {
      key: 'segment',
      render: item => {
        if (item._source.segment) {
          return (
            <BubbleBadge
              key={item._source.segment._id}
              color={item._source.segment.color}
              className="mt-1 mr-1"
            >
              {item._source.segment.name}
            </BubbleBadge>
          );
        }

        return <></>;
      },
    },
    {
      key: 'levels',
      render: item => {
        let element = <></>;

        if (item._source.levels.length > 0) {
          element = (
            <>
              {item._source.levels.map(l => {
                return (
                  <BubbleBadge key={l._id} color={hashStringToColor(l.name)} className="mr-1">
                    {l.name}
                  </BubbleBadge>
                );
              })}
            </>
          );
        }

        return element;
      },
    },
    {
      key: 'attributes',
      render: item => {
        let element = <></>;

        if (item._source.attributes.length > 0) {
          element = (
            <>
              {item._source.attributes.map(a => {
                return (
                  <BubbleBadge key={a._id} color={hashStringToColor(a.name)} className="mr-1">
                    {a.name}
                  </BubbleBadge>
                );
              })}
            </>
          );
        }

        return element;
      },
    },
    {
      key: 'endpoints',
      render: item => <McmEndpoints template={item._source} />,
    },
    {
      key: 'private',
      render: item => {
        let element = <></>;

        if (item._source.isPrivate) {
          element = <FontAwesomeIcon className="text-slate" icon="check" fixedWidth />;
        }

        return element;
      },
    },
    {
      key: 'crew',
      render: item => {
        let element = <></>;

        if (item._source.crewOnly) {
          element = <FontAwesomeIcon className="text-slate" icon="check" fixedWidth />;
        }

        return element;
      },
    },
    {
      key: 'simple',
      render: item => {
        let element = <></>;

        if (item._source.simple) {
          element = <InlineIcon className="w-6 h-6" name={'check'} />;
        }

        return element;
      },
    },
    {
      key: 'duration',
      render: item => {
        return <>{String(item._source.defaultDuration)}</>;
      },
    },
    {
      key: 'images',
      render: item => {
        let element = <>0</>;

        if (item._source.images.length) {
          element = <span className="time-span inline-block">{item._source.images.length}</span>;
        }

        return element;
      },
    },
    {
      key: 'inventoried',
      render: item => {
        let element = <></>;

        if (item._source.inventoried) {
          element = (
            <>
              <InlineIcon className="w-6 h-6" name={'check'} />
            </>
          );
        }

        return element;
      },
    },
    {
      key: 'createdAt',
      render: item => {
        let element = <></>;

        if (item._source.createdAt) {
          const createdAt = getLocaleUTCDateTimeString(new Date(item._source.createdAt));
          const [date, time] = createdAt.split(', ');
          element = (
            <>
              <div className="block whitespace-nowrap">{date}</div>
              <div className="block whitespace-nowrap">{time}</div>
            </>
          );
        }

        return element;
      },
    },
    {
      key: 'actions',
      render: item => (
        <>
          <BubbleButton
            className="p-2-i"
            variant="navy"
            inverse
            icon="edit"
            onClick={() => openUpdateEventTemplateForm(item._source)}
          />
        </>
      ),
    },
  ];

  const visibleTableFields = tableFields.filter(tf =>
    tf._visible !== undefined ? tf._visible : visibleFields[tf.key]
  );
  const toggleableTableFields = tableFields.filter(tf => tf._toggleable);

  function bodyFn() {
    return (
      <>
        <BubbleTable
          responsive
          striped
          headVariant="titanium"
          fields={visibleTableFields}
          items={dataBundle?.eventTemplates}
          keyExtractor={item => item._source._id}
          stickyHeader={182}
          templates={tableTemplates}
          showPretitle
          pretitleClassName="p-0-i"
          showAllItems={true}
          onRowDblClick={item => openUpdateEventTemplateForm(item._source)}
          pretitleRender={() => (
            <div className="px-4 pt-4 pb-1">
              <BubbleDebounce
                onTrigger={[
                  (search: string) => setPaginationParams({ ...paginationParams, page: 1, search }),
                  500,
                ]}
              >
                {({ debounce }) => (
                  <BubbleInput
                    type="text"
                    variant="titanium"
                    block
                    value={paginationParams.search}
                    inputClassName="outline-none-i"
                    onValue={debounce}
                    placeholder="Search..."
                  />
                )}
              </BubbleDebounce>
            </div>
          )}
        />

        {paginationMetadata && (
          <BubbleTablePaginationFooter
            paginationMetadata={paginationMetadata}
            perPage={paginationParams.perPage}
            onLoadMoreClick={() =>
              setPaginationParams({ ...paginationParams, page: paginationParams.page + 1 })
            }
            onPerPageChange={value =>
              setPaginationParams({ ...paginationParams, page: 1, perPage: value })
            }
          />
        )}
      </>
    );
  }

  function headerFn() {
    return <LibrariesSubNavigation active="events" />;
  }

  function sideFn() {
    return (
      <>
        <div className="flex flex-col relative items-stretch p-4 bg-titanium-darker">
          <div className="grid gap-2">
            {showNewEventBtn && (
              <BubbleButton
                variant="lime"
                onClick={openCreateEventTemplateForm}
                className="text-left"
                icon="sparkles"
              >
                Create Event
              </BubbleButton>
            )}

            <BubbleButton
              variant="slate"
              onClick={generateCSV}
              className="text-left"
              icon="file-export"
            >
              Export to CSV
            </BubbleButton>
          </div>
        </div>

        <BubbleAccordion
          variant="titanium"
          className="flex-grow flex flex-col border-t border-forest-darkest"
          labels={['Filters']}
          open={[0]}
          locked={[0]}
        >
          <div className="grid gap-4 p-4">
            <BubbleFormGroup label="Dates" labelClassName="text-white-i">
              <div className="grid grid-cols-1 gap-1">
                <BubbleDebounce
                  onTrigger={[
                    (date: string) => {
                      setPaginationParams({
                        ...paginationParams,
                        page: 1,
                        startDate: date,
                      });
                    },
                    500,
                  ]}
                >
                  {({ debounce }) => (
                    <BubbleInput
                      variant="titanium"
                      type="date"
                      value={paginationParams.startDate}
                      onValue={debounce}
                    />
                  )}
                </BubbleDebounce>
                <BubbleDebounce
                  onTrigger={[
                    (date: string) =>
                      setPaginationParams({ ...paginationParams, page: 1, endDate: date }),
                    500,
                  ]}
                >
                  {({ debounce }) => (
                    <BubbleInput
                      variant="titanium"
                      type="date"
                      value={paginationParams.endDate}
                      onValue={debounce}
                    />
                  )}
                </BubbleDebounce>
              </div>
            </BubbleFormGroup>

            <BubbleFormGroup label="Options" labelClassName="text-white-i">
              <div className="grid gap-1">
                <div className="text-white flex relative">
                  <BubbleInput
                    type="checkbox"
                    variant="titanium"
                    checked={paginationParams.archived}
                    inputClassName="stretched-link"
                    className="mr-1"
                    onValue={(checked: boolean) => {
                      setPaginationParams({ ...paginationParams, page: 1, archived: checked });
                    }}
                  />
                  <span>View Archived</span>
                </div>
              </div>
            </BubbleFormGroup>

            <BubbleFormGroup label="Fields" labelClassName="text-white-i">
              <div className="grid grid-cols-1">
                {toggleableTableFields.map(tf => (
                  <div key={tf.key} className="text-white flex relative py-1">
                    <BubbleInput
                      type="checkbox"
                      variant="titanium"
                      className="mr-1"
                      inputClassName="stretched-link"
                      checked={visibleFields[tf.key]}
                      onValue={chk => setVisibleFields({ ...visibleFields, [tf.key]: chk })}
                    />
                    <span>{tf.label || camelToSentenceCase(tf.key)}</span>
                  </div>
                ))}
              </div>
            </BubbleFormGroup>
          </div>
        </BubbleAccordion>
      </>
    );
  }

  function outerFn() {
    return (
      <>
        {showEventTemplateForm && (
          <Popup title={popupTitle} onClose={closeEventTemplateForm}>
            {selectedEventTemplate && (
              <EventTemplateForm
                user={user}
                mode={eventTemplateFormMode ?? 'create'}
                eventTemplate={selectedEventTemplate}
                staffTemplates={dataBundle?.staffTemplates || []}
                property={property}
                allProperties={properties}
                serverInfo={serverInfo}
                hasTranslations={hasTranslations}
                onSubmit={handleFormSubmit}
                onArchive={handleEventArchive}
                onClone={handleEventClone}
                onEditedEventTemplateChange={handleEditedEventTemplateChange}
              />
            )}
          </Popup>
        )}
        {showAccessForm && (
          <BubbleDrawer
            onClose={() => setShowAccessForm(undefined)}
            containerClassName="bg-eggshell"
          >
            <BubbleDrawerBasicContainer
              title="Edit Event"
              subtitle="Manage Access"
              onClose={() => setShowAccessForm(undefined)}
            >
              <PropertyAccessFormik
                edit={showAccessForm}
                makePropertyAccessItem={itm => ({ property: itm })}
                updateFn={handleFormSubmit}
                actionsAppearance="drawer-float"
                subnavClassName="-mx-8"
              />
            </BubbleDrawerBasicContainer>
          </BubbleDrawer>
        )}
      </>
    );
  }

  async function closeEventTemplateForm(showPrompt = true) {
    let shouldExit = true;

    if (!isSaved && showPrompt) {
      shouldExit = await Alert.question('You have unsaved changes, do you want to exit anyways?');
    }

    if (shouldExit) {
      setShowEventTemplateForm(false);
      setIsSaved(true);
    }
  }

  function openCreateEventTemplateForm() {
    let newEventTemplate: EventTemplate = {
      _id: '',
      title: '',
      description: '',
      levels: [],
      attributes: [],
      incomplete: true,
      defaultDuration: 60,
      source: property._id,
      brand: property.brand,
      properties: [{ property: property._id, defaultStaffAllocations: [] }],
      images: [],
      softConflict: false,
      isPrivate: false,
      crewOnly: false,
      interactiveFeedDisplay: DefaultEventDisplayOption,
      callToActions: [],
    };

    setShowEventTemplateForm(true);
    setSelectedEventTemplate(newEventTemplate);
    setEventTemplateFormMode('create');
  }

  function openUpdateEventTemplateForm(eventTemplate: EventTemplate) {
    setShowEventTemplateForm(true);
    setSelectedEventTemplate(eventTemplate);
    setEventTemplateFormMode('edit');
  }

  async function handleFormSubmit(
    eventTemplate: EventTemplate,
    action?: 'promote' | 'demote',
    files?: File[]
  ) {
    let bareEventTemplate = bare(eventTemplate);

    if (eventTemplateFormMode === 'edit') {
      await update(bareEventTemplate, action);
    } else if (eventTemplateFormMode === 'create') {
      let newEventTemplate: PartialBy<BareEventTemplate, '_id'> = { ...bareEventTemplate };
      delete newEventTemplate._id;
      const et = await create(newEventTemplate);

      if (files?.length) {
        const images = await Promise.all(
          files.map(file => uploadEventImage({ ...eventTemplate, _id: et._id }, file))
        );

        if (images.length) {
          await update({ ...et, images: images.map(image => image.location) });
        }
      }
    }

    await closeEventTemplateForm(false);

    refresh();
  }

  async function handleEventArchive(eventTemplate: EventTemplate) {
    let bareEventTemplate = bare(eventTemplate);
    await setArchived(bareEventTemplate);
    await closeEventTemplateForm(false);
    // no need to re-fetch, only remove the archived one from list
    setDataBundle({
      ...dataBundle,
      eventTemplates: (dataBundle?.eventTemplates || []).filter(
        et => et._source._id !== bareEventTemplate._id
      ),
      staffTemplates: dataBundle?.staffTemplates || [],
    });
  }

  async function handleEventClone(eventTemplate: EventTemplate) {
    const evtTmplCopy = { ...eventTemplate };

    evtTmplCopy.source = property._id;
    delete evtTmplCopy.archived;

    if (evtTmplCopy.interactiveFeedDisplay?.trim() === '') {
      // FIXME: fixes a bug with some event templates not having this set properly
      evtTmplCopy.interactiveFeedDisplay = DefaultEventDisplayOption;
    }

    await closeEventTemplateForm();

    setShowEventTemplateForm(true);
    setSelectedEventTemplate(evtTmplCopy);
    setEventTemplateFormMode('create');
  }

  async function generateCSV() {
    const result = await exportEventTemplatesToCSV(property._id);
    if (result.location) {
      window.open(result.location, '_blank');
    }
  }

  function handleEditedEventTemplateChange(_: EventTemplate) {
    if (isSaved) {
      setIsSaved(false);
    }
  }

  return (
    <Libraries
      user={user}
      active="libraries"
      property={property}
      loading={loading}
      contentRef={contentRef}
      headerFn={headerFn}
      bodyFn={bodyFn}
      outerFn={outerFn}
      sideFn={sideFn}
      {...rest}
    />
  );
}
